// Top-level route table. Auth state lives here so a single useAuth instance
// drives both the login form and the protected gate.

import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth, type AuthState } from './hooks/useAuth';
import { useAutoMarkReadOnVisibility } from './hooks/useAutoMarkReadOnVisibility';
import { LoginPage } from './pages/LoginPage';
import { HomePage } from './pages/HomePage';
import { SettingsPage } from './pages/SettingsPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { DsPortalProvider } from './components/ds/DsPortalProvider';
import { DsToaster } from './components/ds/DsToaster';

export interface AppProps {
  // Sprint access-session-recovery (challenger R1 change 5's corollary) —
  // main.tsx owns stripping `?reauth=` from the URL once the app is past
  // the Access gate (a successful boot); it has no direct line into
  // useAuth's state, so App calls this back whenever `authState` resolves.
  // Optional so every existing render site (`<App />` with no props,
  // including every test) keeps compiling unchanged.
  onAuthResolved?: (state: AuthState) => void;
}

export function App({ onAuthResolved }: AppProps = {}) {
  const { authState, login, logout, meNetworkError } = useAuth();
  useAutoMarkReadOnVisibility();

  useEffect(() => {
    onAuthResolved?.(authState);
  }, [authState, onAuthResolved]);

  return (
    // DS.8 D5.5/D6 — `<DsToaster/>` is mounted HERE, as a SIBLING of the
    // containment root, and both halves of that sentence are load-bearing.
    //
    // HERE, not in `DashboardLayout`: that component is rendered only by the
    // `/` route, while `settings/RunnersTable.tsx` pushes SIX toasts from
    // `/settings`. Every one of them was written to the store and rendered
    // NOWHERE — a pre-existing, live, silent failure this swap had to choose a
    // mount point anyway to fix. Auth-gated rather than a layout route,
    // because a layout route re-mounts its element on `/` <-> `/settings` and
    // would drop the in-flight stack; and gated rather than unconditional so
    // `/login` stays toaster-free.
    //
    // OUTSIDE `<DsPortalProvider>`: that div is `.aos-ds-root`, which carries
    // `isolation: isolate` (index.css, DS.4 D4), and since DS.7 D6 it wraps
    // the whole route table. The DS `ToastContainer` does NOT portal — it
    // renders in place, exactly where sonner did — so a toaster mounted inside
    // would be TRAPPED in that stacking context and paint under every modal,
    // however large its z-index. This is the trap `ds8-z-order.spec.ts`
    // hit-tests in a running browser rather than reading off computed CSS.
    // Mounting outside costs no role value: DS.4 declares the skin at `:root`,
    // not under `.aos-ds-root`.
    //
    // DS.7 D6 — the containment root is EXTENDED to the whole route table,
    // not moved. Measured at open: `App()` returns `<Routes>` (not a DOM
    // node), so `/settings` and the `DocViewerSheet` that `HomePage` renders
    // as a SIBLING of `DashboardLayout` had no `.aos-ds-root` ancestor and no
    // `DsPortalProvider` in their React tree — DS.4 put both on
    // `DashboardLayout`'s shell root. Every DS role still resolved there
    // (the skin is imported on `:root`); what was missing is the containment
    // root and the portal container, which is why a DS overlay opened from
    // `/settings` inherited the route's stacking context and a portalling
    // piece would have landed on `document.body`.
    //
    // `h-full w-full` is LOAD-BEARING, not decoration: `index.css:79-84` sets
    // `html, body, #root { height: 100dvh; overflow: hidden }` and the two
    // route roots (`DashboardLayout.tsx:58`, `SettingsPage.tsx:65`) are both
    // `flex flex-col h-full w-full`. An auto-height div inserted here makes
    // every `h-full` below resolve against `auto`: the shell collapses to
    // content height, `ConversationList`'s `absolute inset-0` loses its
    // containing box and the composer leaves the viewport. No unit test sees
    // that; QA D14d reloads both routes for it.
    //
    // `DashboardLayout`'s own root STAYS. Nesting is harmless — `isolation:
    // isolate` twice is idempotent and DS.4's `.aos-ds-root` rule carries no
    // `z-index` — and React context resolution means a portal opened inside
    // the dashboard still targets the INNER container while one opened from
    // `/settings` targets this outer one. Moving DS.4's class instead of
    // extending it would silently invert a merged sprint's decision.
    <>
    <DsPortalProvider className="h-full w-full" data-testid="app-root">
    <Routes>
      <Route
        path="/login"
        element={
          <LoginPage
            authed={authState === 'authed'}
            gateExpired={authState === 'gate-expired'}
            meNetworkError={meNetworkError}
            login={login}
          />
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute authState={authState}>
            <HomePage logout={logout} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute authState={authState}>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </DsPortalProvider>
    {authState === 'authed' && <DsToaster />}
    </>
  );
}
